/**
 * Created by piotr.pozniak@thebeaverhead.com on 04/12/2024
 *
 * Untitled UI rebuild per assets/handoffs/design_handoff_new_workspace.
 * The "name taken" error state from the handoff is intentionally not
 * implemented — the backend has no uniqueness rule for organization name,
 * so there is nothing to key the error off yet.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Modal from "../theme/untitled/Modal/Modal";
import Input from "../theme/untitled/Input/Input";
import Button from "../theme/untitled/Button/Button";
import Icon from "../theme/untitled/icons/Icon";
import { useOrganizationStore } from "../../../hooks/redux/organization";
import { useUserStore } from "../../../hooks/redux/user";
import { useAppStore } from "../../../hooks/redux/app";
import ApiError from "../component/ApiError";
import styles from "./NewOrganizationSetup.module.scss";

const NewOrganizationSetup = () => {
  const history = useHistory();
  const { user, changeOrganization } = useUserStore();
  const { clearErrors } = useAppStore();
  const {
    organization: { edit, editError, editSuccess, model },
    createOrganization,
  } = useOrganizationStore();

  const [name, setName] = useState("");

  const onClose = useCallback(async () => {
    if (edit) return;
    await clearErrors();
    history.goBack();
  }, [edit, clearErrors, history]);

  const onNameChange = useCallback((e) => setName(e.target.value), []);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (edit || name.trim() === "") return;
      createOrganization({
        name: name.trim(),
        treasurer_email: user.model.email,
        billing_contact: `${user.model.first_name} ${user.model.last_name}`,
      });
    },
    [edit, name, user, createOrganization]
  );

  useEffect(() => {
    if (!editSuccess) return;
    (async () => {
      await changeOrganization(model.uuid);
      history.goBack();
    })();
  }, [editSuccess]);

  const submitDisabled = edit || name.trim() === "";

  return (
    <Modal
      open
      onClose={onClose}
      icon={<Icon name="Building02" size={24} />}
      title="Create a workspace"
      className={styles.panel}
      footer={
        <div className={styles.footerButtons}>
          <Button
            hierarchy="secondaryGray"
            onClick={onClose}
            disabled={edit}
            className={styles.footerButton}
          >
            Cancel
          </Button>
          <Button
            hierarchy="primary"
            type="submit"
            form="new-workspace-form"
            disabled={submitDisabled}
            loading={edit}
            className={styles.footerButton}
          >
            {edit ? "Creating…" : "Create workspace"}
          </Button>
        </div>
      }
    >
      <p className={styles.lede}>
        A workspace is a separate account with its own billing, team and
        branding. Useful for agencies and teams running several brands.
      </p>
      <form id="new-workspace-form" className={styles.form} onSubmit={onSubmit}>
        <Input
          label="Workspace name"
          required
          placeholder="Workspace name"
          hint="Shown in the workspace switcher and on shared widgets."
          value={name}
          onChange={onNameChange}
          disabled={edit}
          autoFocus
          autoComplete="off"
        />
      </form>
      <ApiError error={editError} className={styles.submitError} />
    </Modal>
  );
};

export default NewOrganizationSetup;
